import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { adminService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export function AdminUserDetail() {
  const { user: authUser, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const userId = Number(id);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [bookings, setBookings] = useState<any[]>([]);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawAdminNotes, setWithdrawAdminNotes] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferUsers, setTransferUsers] = useState<any[]>([]);
  const [transferTargetId, setTransferTargetId] = useState<number | null>(null);
  const [transferSearch, setTransferSearch] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'volunteer' as 'guest' | 'volunteer' | 'supervisor' | 'admin',
    withdrawn: false,
    require_doctors_note: false,
    not_found_on_whatsapp: false,
    application_status: 'pending',
    full_name: '',
    phone: '',
    date_of_birth: '',
    dtu_member_status: 'no' as 'yes_student_employee' | 'yes_alumni' | 'no',
    previous_volunteer: '',
    has_peoplevol_profile: null as boolean | null,
    confirmed_peoplevol_registration: null as boolean | null,
  });

  useEffect(() => {
    if (authLoading) return;
    if (!authUser) {
      navigate('/login');
      return;
    }
    if (authUser.role !== 'admin') {
      navigate('/');
      return;
    }
    if (!userId) {
      setError('Invalid user id');
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const [userRes, bookingsRes] = await Promise.all([
          adminService.getUser(userId),
          adminService.getUserBookingsAdmin(userId),
        ]);
        const data = userRes.data;
        setFormData({
          email: data.email || '',
          name: data.name || '',
          role: data.role || 'volunteer',
          withdrawn: !!data.withdrawn,
          require_doctors_note: !!data.require_doctors_note,
          not_found_on_whatsapp: !!data.not_found_on_whatsapp,
          application_status: data.application_status || 'pending',
          full_name: data.full_name || '',
          phone: data.phone || '',
          date_of_birth: data.date_of_birth ? String(data.date_of_birth).substring(0, 10) : '',
          dtu_member_status: data.dtu_member_status || (data.dtu_member ? 'yes_student_employee' : 'no'),
          previous_volunteer: data.previous_volunteer || '',
          has_peoplevol_profile: typeof data.has_peoplevol_profile === 'boolean' ? data.has_peoplevol_profile : null,
          confirmed_peoplevol_registration: typeof data.confirmed_peoplevol_registration === 'boolean' ? data.confirmed_peoplevol_registration : null,
        });
        setBookings(bookingsRes.data || []);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load user');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [authLoading, authUser, navigate, userId]);

  const formatPhoneForDisplay = (phone: string): string => {
    const parsed = parsePhoneNumberFromString(phone, 'DK');
    if (parsed) {
      return parsed.formatInternational();
    }
    return phone;
  };

  const normalizePhoneForSubmit = (phone: string): string => {
    const parsed = parsePhoneNumberFromString(phone, 'DK');
    if (parsed) {
      return parsed.number;
    }
    return phone.trim();
  };

  const handleWithdraw = async () => {
    setWithdrawing(true);
    setError('');
    try {
      const res = await adminService.withdrawUser(userId, withdrawAdminNotes.trim() || undefined);
      setFormData((p) => ({ ...p, withdrawn: true, role: 'guest', application_status: 'withdrawn' }));
      setBookings([]);
      setShowWithdrawModal(false);
      setWithdrawAdminNotes('');
      const count = res.data.deleted_bookings;
      alert(`Volunteer withdrawn. ${count} future shift${count !== 1 ? 's' : ''} removed.`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to withdraw volunteer');
    } finally {
      setWithdrawing(false);
    }
  };

  const openTransferModal = async () => {
    try {
      const res = await adminService.getUsers();
      setTransferUsers((res.data || []).filter((u: any) =>
        u.id !== userId &&
        !u.withdrawn &&
        ['volunteer', 'supervisor', 'admin'].includes(u.role)
      ));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load users');
      return;
    }
    setTransferTargetId(null);
    setTransferSearch('');
    setShowTransferModal(true);
  };

  const handleTransfer = async () => {
    if (!transferTargetId) return;
    const target = transferUsers.find((u) => u.id === transferTargetId);
    const shiftIds = bookings.map((b) => b.shift_id);
    if (!confirm(`Transfer ${bookings.length} shift${bookings.length !== 1 ? 's' : ''} from ${formData.full_name || formData.name} to ${target?.full_name || target?.name}?`)) return;
    setTransferring(true);
    setError('');
    try {
      for (const b of bookings) {
        await adminService.assignVolunteer({ user_id: transferTargetId, shift_id: b.shift_id, assigned_role: b.assigned_role });
      }
      for (const b of bookings) {
        await adminService.unassignVolunteer(b.booking_id);
      }
      setBookings([]);
      setShowTransferModal(false);
      alert(`${shiftIds.length} shift${shiftIds.length !== 1 ? 's' : ''} transferred to ${target?.full_name || target?.name}.`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const handleUnassign = async (bookingId: number, shiftLabel: string) => {
    if (!confirm(`Unassign from "${shiftLabel}"?`)) return;
    try {
      await adminService.unassignVolunteer(bookingId);
      setBookings((prev) => prev.filter((b) => b.booking_id !== bookingId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to unassign');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await adminService.updateUser(userId, {
        role: formData.role,
        name: formData.name,
        withdrawn: formData.withdrawn,
        require_doctors_note: formData.require_doctors_note,
        not_found_on_whatsapp: formData.not_found_on_whatsapp,
        application_status: formData.application_status,
        full_name: formData.full_name,
        phone: normalizePhoneForSubmit(formData.phone),
        date_of_birth: formData.date_of_birth,
        dtu_member_status: formData.dtu_member_status,
        previous_volunteer: formData.previous_volunteer,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <>
        <Header user={authUser} onLogout={logout} />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">Loading...</div>
        </main>
      </>
    );
  }

  return (
    <>
      <Header user={authUser} onLogout={logout} />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Details</h1>
            <p className="text-sm text-gray-600">Manage user profile and assigned shifts.</p>
          </div>
          <Link to="/admin?tab=users" className="text-blue-600 hover:underline text-sm">
            ← Back to Users
          </Link>
        </div>

        {error && <div className="bg-red-100 text-red-800 p-4 rounded mb-4">{error}</div>}

        <section className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                disabled
                className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                className="w-full border rounded px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData((p) => ({ ...p, full_name: e.target.value }))}
                className="w-full border rounded px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                onBlur={() => setFormData((p) => ({ ...p, phone: formatPhoneForDisplay(p.phone) }))}
                className="w-full border rounded px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              <input
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData((p) => ({ ...p, date_of_birth: e.target.value }))}
                className="w-full border rounded px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData((p) => ({ ...p, role: e.target.value as any }))}
                className="w-full border rounded px-3 py-2"
              >
                <option value="guest">guest</option>
                <option value="volunteer">volunteer</option>
                <option value="supervisor">supervisor</option>
                <option value="admin">admin</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Application Status</label>
              <select
                value={formData.application_status}
                onChange={(e) => setFormData((p) => ({ ...p, application_status: e.target.value }))}
                className="w-full border rounded px-3 py-2"
              >
                <option value="pending">pending</option>
                <option value="approved">approved</option>
                <option value="rejected">rejected</option>
                <option value="withdrawn">withdrawn</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Previous Volunteer</label>
              <select
                value={formData.previous_volunteer}
                onChange={(e) => setFormData((p) => ({ ...p, previous_volunteer: e.target.value }))}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">No</option>
                <option value="2023">2023</option>
                <option value="2024">2024</option>
                <option value="2025">2025</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">DTU Membership</label>
              <select
                value={formData.dtu_member_status}
                onChange={(e) => setFormData((p) => ({ ...p, dtu_member_status: e.target.value as any }))}
                className="w-full border rounded px-3 py-2"
              >
                <option value="yes_student_employee">Yes (Student, Employee, ...)</option>
                <option value="yes_alumni">Yes (Alumni)</option>
                <option value="no">No</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PeopleVol Profile</label>
              <div className="w-full border rounded px-3 py-2 bg-gray-50 text-gray-700">
                {formData.has_peoplevol_profile === null
                  ? 'Unknown'
                  : formData.has_peoplevol_profile
                    ? 'Has profile'
                    : 'No profile'}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PeopleVol Registration</label>
              <div className="w-full border rounded px-3 py-2 bg-gray-50 text-gray-700">
                {formData.confirmed_peoplevol_registration === null
                  ? 'Unknown'
                  : formData.confirmed_peoplevol_registration
                    ? 'Confirmed'
                    : 'Not confirmed'}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.require_doctors_note}
                onChange={(e) => setFormData((p) => ({ ...p, require_doctors_note: e.target.checked }))}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-700">Require doctor note for future</span>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.not_found_on_whatsapp}
                onChange={(e) => setFormData((p) => ({ ...p, not_found_on_whatsapp: e.target.checked }))}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-700">Not found on WhatsApp</span>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.withdrawn}
                onChange={(e) => setFormData((p) => ({ ...p, withdrawn: e.target.checked }))}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-700">Withdrawn</span>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  try {
                    await adminService.notifyShiftsUpdated(userId);
                    alert('Email sent successfully');
                  } catch (err: any) {
                    setError(err.response?.data?.error || 'Failed to send email');
                  }
                }}
                className="px-4 py-2 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 font-medium text-sm"
              >
                Notify: Shifts Updated
              </button>
              {!formData.withdrawn && (
                <button
                  onClick={() => setShowWithdrawModal(true)}
                  className="px-4 py-2 rounded bg-red-100 text-red-700 hover:bg-red-200 font-medium text-sm"
                >
                  Withdraw Volunteer
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Assigned Shifts</h2>
            {bookings.length > 0 && (
              <button
                onClick={openTransferModal}
                className="px-3 py-1.5 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 font-medium text-sm"
              >
                Transfer Shifts
              </button>
            )}
          </div>
          {bookings.length === 0 ? (
            <p className="text-gray-600">No assigned shifts.</p>
          ) : (
            <div className="space-y-3">
              {bookings.map((b) => {
                const start = new Date(b.start_time);
                const end = new Date(b.end_time);
                const isNextDay = start.toDateString() !== end.toDateString();
                const fmt = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
                const dateLabel = start.toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric' });
                const attendanceStatus = b.attendance_status || 'pending';
                return (
                  <div key={b.booking_id} className="border rounded p-3 bg-gray-50 flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-900">
                        {dateLabel} · {fmt(start)}–{fmt(end)}{isNextDay ? ' (next day)' : ''}
                      </p>
                      <p className="text-xs text-gray-600">{b.title} · {b.location || 'Meeting point TBD'}</p>
                      <p className="text-xs text-gray-600 mt-1">Status: {attendanceStatus}</p>
                    </div>
                    <button
                      onClick={() => handleUnassign(b.booking_id, `${b.title} ${dateLabel}`)}
                      className="text-sm text-red-600 hover:text-red-800 whitespace-nowrap flex-shrink-0"
                    >
                      Unassign
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-purple-700 mb-2">Transfer Shifts</h3>
            <p className="text-sm text-gray-700 mb-3">
              Select a volunteer to receive these shifts from <strong>{formData.full_name || formData.name}</strong>. The current volunteer will be unassigned from all of them.
            </p>
            <div className="border rounded divide-y mb-4 max-h-40 overflow-y-auto bg-gray-50">
              {bookings.map((b) => {
                const start = new Date(b.start_time);
                const fmt = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
                const dateLabel = start.toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric' });
                return (
                  <div key={b.booking_id} className="flex items-center justify-between px-3 py-1.5 text-sm">
                    <span className="text-gray-800">{dateLabel} {fmt(start)} · {b.title}</span>
                    <span className={`ml-2 px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
                      (b.assigned_role || 'volunteer') === 'volunteer' ? 'bg-orange-100 text-orange-800' :
                      (b.assigned_role || 'volunteer') === 'supervisor' ? 'bg-yellow-100 text-yellow-800' :
                      (b.assigned_role || 'volunteer') === 'admin' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-700'
                    }`}>{b.assigned_role || 'volunteer'}</span>
                  </div>
                );
              })}
            </div>
            <input
              type="text"
              value={transferSearch}
              onChange={(e) => setTransferSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full border rounded px-3 py-2 text-sm mb-3 focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            />
            <div className="max-h-60 overflow-y-auto border rounded divide-y mb-4">
              {transferUsers
                .filter((u) => {
                  const q = transferSearch.toLowerCase();
                  return !q || (u.full_name || u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                })
                .map((u) => (
                  <button
                    key={u.id}
                    onClick={() => setTransferTargetId(u.id)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-purple-50 ${transferTargetId === u.id ? 'bg-purple-100 font-semibold' : ''}`}
                  >
                    {u.full_name || u.name} <span className="text-gray-500">{u.email}</span>
                  </button>
                ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleTransfer}
                disabled={!transferTargetId || transferring}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-300 font-medium"
              >
                {transferring ? 'Transferring...' : 'Confirm Transfer'}
              </button>
              <button
                onClick={() => setShowTransferModal(false)}
                disabled={transferring}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-red-700 mb-2">Withdraw Volunteer</h3>
            <p className="text-sm text-gray-700 mb-4">
              This will permanently withdraw <strong>{formData.full_name || formData.name || formData.email}</strong>,
              remove them from all future shifts, set their role to guest, and send them a withdrawal confirmation email.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin notes (optional)</label>
              <textarea
                value={withdrawAdminNotes}
                onChange={(e) => setWithdrawAdminNotes(e.target.value)}
                rows={3}
                className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-red-400 focus:border-transparent"
                placeholder="Reason or notes for the audit log..."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleWithdraw}
                disabled={withdrawing}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 font-medium"
              >
                {withdrawing ? 'Withdrawing...' : 'Confirm Withdrawal'}
              </button>
              <button
                onClick={() => { setShowWithdrawModal(false); setWithdrawAdminNotes(''); }}
                disabled={withdrawing}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
